/* eslint-disable no-fallthrough */
/* eslint-disable camelcase */
import { Weekday, Options, RRule, RRuleSet, rrulestr } from 'rrule';
import { FormData } from './AddEventModal/eventTypes';
import {
  Appointment,
  notes,
  TherapistData,
  TherapistRuleData,
} from './calendarTypes';
import { CalendarView } from '../../constants/CalendarEnums';
import {
  RepeatType,
  EndAfterType,
  EventType,
} from '../../constants/EventEnums';
import moment from 'moment';
import { weekDays } from './calendarConstants';
import { EditEventOption } from './edit-event-modal/ModalConstants';
import { DeleteEventOption } from './DeleteEventModals/ModalConstants';
import { commaSeparatedStringToObject } from 'src/legacy/utils/helpers';

/**
 *
 * Converts an ISO date string to have 00:00:00 as the time portion
 * @param isoString - ISO date string (e.g., "2026-01-06T14:30:45.123Z")
 * @returns ISO string with time set to 00:00:00 (e.g., "2026-01-06T00:00:00.123Z")
 */

export const formatToMidnightISO = (isoString: string): string => {
  return isoString.replace(/T(\d{2}:\d{2}:\d{2})/, 'T00:00:00');
};

interface ParsedRRule {
  recurring: boolean;
  repeat: RepeatType;
  repeat_every: number;
  repeat_days: string[];
  end_after?: EndAfterType; // "count" | "until"
  repeat_end_after: {
    count?: number;
    date?: string;
  };
  exdates?: string[]; // keep exclusions clean & separate
  rruleSet?: RRuleSet; // optional, lets consumers get actual dates
}

export const parseRRuleString = (rruleString: string): ParsedRRule => {
  if (!rruleString) {
    return {
      recurring: false,
      repeat: RepeatType.Never,
      repeat_every: 1,
      repeat_days: [],
      repeat_end_after: {},
    };
  }

  const rruleSet = rrulestr(rruleString, { forceset: true }) as RRuleSet;
  const rules = rruleSet.rrules();
  const exdates = rruleSet.exdates();

  if (!rules.length) {
    return {
      recurring: false,
      repeat: RepeatType.Never,
      repeat_every: 1,
      repeat_days: [],
      repeat_end_after: {},
      rruleSet, // still return the empty set for consistency
    };
  }

  const rule = rules[0];
  const options = rule.options;

  // COUNT=1 → treat as single event (not discard)
  if (options.count === 1) {
    return {
      recurring: false,
      repeat: RepeatType.Never,
      repeat_every: 1,
      repeat_days: [],
      repeat_end_after: { count: 1 },
      exdates: exdates.map(d => d.toISOString()),
      rruleSet,
    };
  }

  const result: ParsedRRule = {
    recurring: true,
    repeat: RepeatType.Never,
    repeat_every: options.interval || 1,
    repeat_days: [],
    repeat_end_after: {},
    exdates: exdates.map(d => d.toISOString()),
    rruleSet,
  };

  // Frequency
  switch (options.freq) {
    case RRule.DAILY:
      result.repeat = RepeatType.Daily;
      break;
    case RRule.WEEKLY:
      result.repeat = RepeatType.Weekly;

      if (options.byweekday) {
        const weekdays: (number | Weekday)[] = Array.isArray(options.byweekday)
          ? options.byweekday
          : [options.byweekday];

        result.repeat_days = weekdays.map(d =>
          typeof d === 'number' ? weekDays[d] : weekDays[d.weekday],
        );
      }

      break;
    default:
      result.repeat = RepeatType.Never;
  }

  // End condition
  if (options.count) {
    result.end_after = EndAfterType.Count;
    result.repeat_end_after.count = options.count;
  } else if (options.until) {
    result.end_after = EndAfterType.Until;
    result.repeat_end_after.date = moment(options.until).toISOString();
  }

  return result;
};

// Define a unified input type for flexibility
type TherapistInputData = {
  [therapistId: string]: {
    therapist_details: any;
    slot_data?: TherapistData['slot_data'];
    rule_data?: TherapistRuleData['rule_data'];
  };
};

interface ExtendedFormData extends FormData {
  exdate?: string;
}

export const createRecurrenceRule = (
  formData: ExtendedFormData,
  oldRule?: string,
): string | null => {
  const weekdayMap: Record<string, Weekday> = {
    SUNDAY: RRule.SU,
    MONDAY: RRule.MO,
    TUESDAY: RRule.TU,
    WEDNESDAY: RRule.WE,
    THURSDAY: RRule.TH,
    FRIDAY: RRule.FR,
    SATURDAY: RRule.SA,
  };

  const rruleSet = new RRuleSet();
  const startDateTime = formData.start_date_time;

  const options: Partial<Options> = {
    interval: Number(formData.repeat_every) || 1,
  };

  switch (formData.repeat) {
    case RepeatType.Daily:
      options.freq = RRule.DAILY;
      break;
    case RepeatType.Weekly:
      options.freq = RRule.WEEKLY;

      if (formData.repeat_days?.length) {
        options.byweekday = formData.repeat_days.map(
          day => weekdayMap[day.toUpperCase()],
        );
      }

      break;
    case RepeatType.Never:
      options.freq = RRule.DAILY;
      options.count = 1;
      break;
  }

  // End condition
  if (formData.end_after === 'count' && formData.repeat_end_after?.count) {
    options.count = formData.repeat_end_after.count;
  } else if (
    formData.end_after === 'until' &&
    formData.repeat_end_after?.date
  ) {
    options.until = new Date(formData.repeat_end_after.date);
  }

  // Start date/time
  if (startDateTime) {
    options.dtstart = new Date(startDateTime); // safer than toLocaleString()
  }

  try {
    const rule = new RRule(options as Options);

    rruleSet.rrule(rule);

    // Always preserve and accumulate existing EXDATEs from oldRule
    if (oldRule?.includes('EXDATE')) {
      const lines = oldRule.split('\n');

      for (const line of lines) {
        if (line.startsWith('EXDATE:')) {
          const dates = line.replace('EXDATE:', '').split(',');

          for (const dateStr of dates) {
            const exdate = new Date(dateStr);

            if (!isNaN(exdate.getTime())) {
              rruleSet.exdate(exdate);
            }
          }
        }
      }
    }

    // Then add any new EXDATE(s) from formData
    if (formData.exdate) {
      const exdates = Array.isArray(formData.exdate)
        ? formData.exdate
        : [formData.exdate];

      for (const ex of exdates) {
        const exdate = new Date(ex);

        if (!isNaN(exdate.getTime())) {
          rruleSet.exdate(exdate);
        }
      }
    }

    return rruleSet.toString() || null;
  } catch (error) {
    console.error('Error creating recurrence rule:', error);

    return null;
  }
};

export const convertAppointmentsMerged = (
  data: TherapistInputData | TherapistData,
  currentView: string = CalendarView.Week,
  startDate: string,
  isGoogleSynced = false,
): Appointment[] => {
  const appointments: Appointment[] = [];

  const startDateAsDate = moment(startDate).startOf('day').toDate();

  let endDate: Date;

  switch (currentView) {
    case CalendarView.Week:
      endDate = moment(startDate).add(8, 'days').toDate();
      break;
    case CalendarView.Month:
      endDate = moment(startDate).endOf('month').toDate();
      break;
    default:
      endDate = moment(startDate).add(8, 'days').toDate(); // Default to Week
  }

  for (const [therapistId, details] of Object.entries(data)) {
    if (!details || typeof details !== 'object') continue;

    const { therapist_details, rule_data, slot_data } = details;

    // -----------------
    // Handle rule_data
    // -----------------
    if (rule_data && Array.isArray(rule_data)) {
      let idCounter = 1;

      rule_data.forEach(ruleItem => {
        if (!ruleItem || !ruleItem.metadata) return;

        const { metadata } = ruleItem;

        // Convert metadata.note to an object when it's a string and mark conversion
        if (metadata && metadata.note) {
          if (typeof metadata.note === 'string') {
            // Try generic string->object first, fallback to comma-separated parser
            metadata.note = commaSeparatedStringToObject(metadata.note);
          }
        }

        // skip opening slots without rules
        if (
          (metadata.r_rule === '' &&
            metadata.slot_type === EventType.Opening) ||
          ruleItem.is_active === false
        )
          return;

        const dtStart = new Date(metadata.slot_start_date_time);

        if (isNaN(dtStart.getTime())) return;

        let occurrenceDates: Date[] = [];

        if (metadata.r_rule) {
          try {
            const decodeRrule = metadata.r_rule.replace(/\/n/g, '\n');
            const parsed = parseRRuleString(decodeRrule);

            if (parsed.rruleSet) {
              const primaryoccurrenceDates = parsed.rruleSet.between(
                startDateAsDate,
                endDate,
                true,
              );

              occurrenceDates = filterUnavailableDates(
                primaryoccurrenceDates,
                parsed.exdates,
              );
            } else {
              occurrenceDates = [dtStart];
            }
          } catch (error) {
            console.error('Error parsing rRule:', error);
            occurrenceDates = [dtStart];
          }
        } else {
          occurrenceDates = [dtStart];
        }

        occurrenceDates.forEach(occurrence => {
          const startTime = occurrence;
          const endTime = new Date(
            startTime.getTime() + metadata.duration * 60000,
          );

          appointments.push({
            Id: idCounter,
            therapistId,
            therapistDetails: therapist_details,
            title: metadata.slot_reason_type,
            start: startTime,
            end: endTime,
            eventId: ruleItem.id,
            eventType: metadata.slot_type,
            eventDuration: metadata.duration,
            isRecurrent: !!metadata.r_rule,
            clientName: metadata.client_name || '',
            clientId: metadata.client_id || '',
            slot_time_str: metadata.slot_time_str || '',
            ...metadata,
          });
          idCounter += 1;
        });
      });
    }

    // -----------------
    // Handle slot_data
    // -----------------
    else if (slot_data && typeof slot_data === 'object') {
      for (const [date, slots] of Object.entries(slot_data)) {
        let idCounter = 1;

        if (!slots || !Array.isArray(slots)) continue;

        slots.forEach(slot => {
          if (!slot || !slot.slot_time_str || !slot.slot_reason_type) return;

          if (
            slot.slot_type === EventType.Opening &&
            (!slot.r_rule || slot.r_rule === '') &&
            !isGoogleSynced
          )
            return;

          const utcTime = new Date(`${date} ${slot.slot_time_str}`);

          if (isNaN(utcTime.getTime())) return;

          const localOffset = utcTime.getTimezoneOffset() * 60000;
          const startTime = new Date(utcTime.getTime() - localOffset);
          const endTime = new Date(startTime.getTime() + slot.duration * 60000);

          if (slot.metadata && typeof slot.metadata === 'string') {
            slot.metadata = JSON.parse(slot.metadata);
          }

          appointments.push({
            Id: idCounter,
            therapistId,
            therapistDetails: therapist_details,
            title: slot.slot_reason_type,
            start: startTime,
            end: endTime,
            eventId: slot.id,
            eventType: slot.slot_type,
            eventDuration: slot.duration,
            isRecurrent: slot.recurrent,
            clientName: slot.client_name || '',
            clientId: slot.client_id || '',
            ...slot,
            ...(slot.metadata || {}),
          });
          idCounter += 1;
        });
      }
    }
  }

  // Convert note string to object for each appointment
  const appointmentsWithNotes = appointments.map(appointment => {
    const note = appointment.note;

    if (!note) return appointment;

    // If note is already an object, keep as-is
    if (typeof note === 'object') {
      return {
        ...appointment,
        note: note as notes,
      };
    }

    // Only pass strings (or undefined) to commaSeparatedStringToObject
    if (typeof note === 'string') {
      const convertedNote = commaSeparatedStringToObject(note) as notes;

      return {
        ...appointment,
        note: convertedNote,
      };
    }

    // Fallback: convert other types to string then parse
    const convertedNote = commaSeparatedStringToObject(String(note)) as notes;

    return {
      ...appointment,
      note: convertedNote,
    };
  });

  return appointmentsWithNotes;
};

export const isRecurring = (rRuleString: string | undefined) => {
  if (!rRuleString) return false;

  // Split the RRULE string by ';'
  const parts = rRuleString.split(';');

  let freq = null;
  let count = null;

  parts.forEach(part => {
    if (part.startsWith('FREQ=')) {
      freq = part.substring(5);
    } else if (part.startsWith('COUNT=')) {
      count = parseInt(part.substring(6), 10);
    }
  });

  // If no frequency specified, not recurring
  if (!freq) return false;

  // If COUNT is 1, treat as not recurring
  if (count === 1) return false;

  // Otherwise, if frequency exists and count is not 1, it is recurring
  return true;
};

interface RuleResult {
  update_r_rule: string | null;
  new_r_rule: string | null;
}

export async function buildUpdatedRules(
  scope: string | null,
  formData: FormData,
  originalRRule?: string,
  startDateChanged = '',
): Promise<RuleResult> {
  const result: RuleResult = { update_r_rule: null, new_r_rule: null };

  switch (scope) {
    case DeleteEventOption.DeleteSingleEvent:

    case EditEventOption.EditSingleEvent: {
      if (!originalRRule) break;

      // Find existing lines
      const lines = originalRRule.split('\n');
      const rruleLine =
        lines.find(line => line.trim().startsWith('RRULE:')) || '';
      let dtStartLine =
        lines.find(line => line.trim().startsWith('DTSTART:')) || '';
      const exdateLine =
        lines.find(line => line.trim().startsWith('EXDATE:')) || '';

      // Prepare new exclusion date for EXDATE line, in UTC format (YYYYMMDDTHHmmssZ)
      const pad = (n: number) => n.toString().padStart(2, '0');
      const getUTCString = (d: Date) =>
        d.getUTCFullYear() +
        pad(d.getUTCMonth() + 1) +
        pad(d.getUTCDate()) +
        'T' +
        pad(d.getUTCHours()) +
        pad(d.getUTCMinutes()) +
        pad(d.getUTCSeconds()) +
        'Z';

      const selectedDate = new Date(formData.start_date_time);
      const exdateStr = getUTCString(selectedDate);

      // Combine with old EXDATE(s), comma separated
      let newExdateLine = '';

      if (exdateLine) {
        // Already has EXDATE(s)
        const exdates = exdateLine
          .replace('EXDATE:', '')
          .split(',')
          .map(s => s.trim())
          .filter(Boolean);

        if (!exdates.includes(exdateStr)) exdates.push(exdateStr); // avoid dupes
        newExdateLine = 'EXDATE:' + exdates.join(',');
      } else {
        // No old EXDATE, just add this one
        newExdateLine = 'EXDATE:' + exdateStr;
      }

      // Fallback or recalculate DTSTART if missing
      if (!dtStartLine && formData.start_date_time) {
        const dt = new Date(formData.start_date_time);

        dtStartLine = 'DTSTART:' + getUTCString(dt);
      }

      // Assemble multi-line update_r_rule string for old series
      result.update_r_rule = [dtStartLine, rruleLine, newExdateLine]
        .filter(Boolean)
        .join('\n');

      if (scope === EditEventOption.EditSingleEvent) {
        // ---- 2. Create new one-off event (COUNT=1) ----
        const singleEventRule: FormData = {
          ...formData,
          recurring: true,
          repeat: RepeatType.Daily,
          repeat_every: 1,
          repeat_days: [],
          end_after: EndAfterType.Count,
          repeat_end_after: { count: 1 },
        };

        result.new_r_rule = await createRecurrenceRule(singleEventRule);
      } else {
        result.new_r_rule = '';
      }

      break;
    }

    case DeleteEventOption.DeleteFutureEvent:

    case EditEventOption.EditFutureEvent: {
      if (!originalRRule) break;

      // eventDate in UTC (recommended for recurrence logic consistency)
      const eventDate = moment.utc(formData.start_date_time); // parses ISO string in UTC
      const dayBefore = eventDate.clone().subtract(1, 'day').endOf('day');
      const originalRuleParsed = parseRRuleString(
        originalRRule.replaceAll('/n', '\n'),
      );
      // const dtStart = extractDtStart(originalRRule);
      // 1. Rule for past events (until day before selected)
      const pastEventsRule: FormData = {
        ...formData,
        repeat: originalRuleParsed.repeat,
        repeat_every: originalRuleParsed.repeat_every,
        repeat_days: originalRuleParsed.repeat_days,
        start_date_time: getDtStartAsISO(originalRRule) || '', // get original series start
        end_after: EndAfterType.Until,
        repeat_end_after: { date: dayBefore.toString() }, // End 1 day before, at end of that day (in UTC)
        recurring: true,
      };

      result.update_r_rule = await createRecurrenceRule(
        pastEventsRule,
        scope === DeleteEventOption.DeleteFutureEvent ? formData.r_rule : '',
      );

      if (scope === EditEventOption.EditFutureEvent) {
        // 2. Rule for future events (new series starting at selected event)
        const futureEventsRule = { ...formData, recurring: true };

        result.new_r_rule = await createRecurrenceRule(futureEventsRule);
      } else {
        result.new_r_rule = null;
      }

      break;
    }

    case DeleteEventOption.DeleteWholeSeries:

    case EditEventOption.EditWholeSeries: {
      if (scope === DeleteEventOption.DeleteWholeSeries) {
        result.update_r_rule = '';
        result.new_r_rule = '';
        break;
      }

      if (!originalRRule) break;

      const wholeSeriesRule = {
        ...formData,
        recurring: true,
        start_date_time:
          startDateChanged === ''
            ? getDtStartAsISO(originalRRule)
            : getDtStartAsISO(startDateChanged),
      };

      const partialRuleBlock = await createRecurrenceRule(
        wholeSeriesRule,
        // originalRRule,
      );

      result.update_r_rule = partialRuleBlock;
      break;
    }

    default: {
      // ALL (non-recurrent or direct full update)
      if (originalRRule) {
        const wholeRule = { ...formData, recurring: true };

        result.update_r_rule = await createRecurrenceRule(
          wholeRule,
          originalRRule,
        );
      }

      result.new_r_rule = '';
    }
  }

  return result;
}

const filterUnavailableDates = (
  available: any[],
  unavailable: string[] | undefined = [],
) => {
  // Convert unavailable dates to just the date string part for comparison
  const unavailableDatesOnly = unavailable.map(
    (dateStr: string | number | Date) =>
      new Date(dateStr).toISOString().slice(0, 10),
  );

  return available.filter((dateStr: string | number | Date) => {
    const dateOnly = new Date(dateStr).toISOString().slice(0, 10);
    // Return true if dateOnly is not in unavailableDatesOnly

    return !unavailableDatesOnly.includes(dateOnly);
  });
};

export const getDtStartAsISO = (rruleString: string): string => {
  // Match DTSTART from the rule
  const match = rruleString.match(/DTSTART:(\d{8}T\d{6}Z)/);

  if (!match) {
    return ''; // No DTSTART found
  }

  const rawDate = match[1]; // e.g. 20250928T103000Z

  // Convert to valid ISO string
  const formatted = rawDate.replace(
    /(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z/,
    '$1-$2-$3T$4:$5:$6.000Z',
  );

  return formatted;
};

export function getUpdatedRRuleDtstart(
  rruleString: string | undefined,
  oldStart: string,
  newStart: string,
): string {
  const dtstartMatch = rruleString
    ? rruleString.match(/DTSTART:([^\n\r]+)/)
    : null;

  if (!dtstartMatch) return '';

  const originalDtstart = dtstartMatch[1]; // e.g., 20250821T183924Z
  const oldDate = new Date(oldStart);
  const newDate = new Date(newStart);

  const originalDate = new Date(
    originalDtstart.replace(
      /(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z/,
      '$1-$2-$3T$4:$5:$6Z',
    ),
  );

  // Check changes
  const dateChanged =
    newDate.getFullYear() !== oldDate.getFullYear() ||
    newDate.getMonth() !== oldDate.getMonth() ||
    newDate.getDate() !== oldDate.getDate();

  const timeChanged =
    newDate.getHours() !== oldDate.getHours() ||
    newDate.getMinutes() !== oldDate.getMinutes();

  if (!dateChanged && !timeChanged) {
    return ''; // nothing changed
  }

  // Break original into components
  const pad = (n: number) => String(n).padStart(2, '0');
  let year = originalDate.getUTCFullYear();
  let month = pad(originalDate.getUTCMonth() + 1);
  let day = pad(originalDate.getUTCDate());
  let hours = pad(originalDate.getUTCHours());
  let minutes = pad(originalDate.getUTCMinutes());
  let seconds = pad(originalDate.getUTCSeconds());

  if (dateChanged) {
    year = newDate.getUTCFullYear();
    month = pad(newDate.getUTCMonth() + 1);
    day = pad(newDate.getUTCDate());
  }

  if (timeChanged) {
    hours = pad(newDate.getUTCHours());
    minutes = pad(newDate.getUTCMinutes());
    seconds = pad(newDate.getUTCSeconds());
  }

  const newDtstart = `${year}${month}${day}T${hours}${minutes}${seconds}Z`;

  return rruleString ? rruleString.replace(originalDtstart, newDtstart) : '';
}

export function appointmentToFormData(appointment: Appointment): FormData {
  const parsedRule = parseRRuleString(
    appointment.r_rule.replaceAll('/n', '\n'),
  );

  return {
    event_type: appointment.eventType,
    therapist_name:
      appointment.therapistName ?? appointment.therapistDetails?.name ?? '',
    service_types: appointment.slot_reason_type,
    patient_age: appointment.patientAge,
    meeting_pref: appointment.meetingPref ?? '',
    start_date_time: new Date(appointment.start).toISOString(),
    duration: appointment.duration ?? appointment.eventDuration,
    office_location: appointment.office_location ?? '',
    allDayEvent: appointment.allDayEvent ?? false,
    recurring: parsedRule.recurring,
    therapist_id: appointment.therapistId,
    repeat: parsedRule.repeat,
    room: appointment.room ?? '',
    repeat_every: parsedRule.repeat_every,
    repeat_days: parsedRule.repeat_days,
    until: parsedRule.repeat_end_after?.date ?? '',
    repeat_end_after: parsedRule.repeat_end_after,
    end_after: parsedRule.end_after,
    r_rule: appointment.r_rule.replaceAll('/n', '\n') ?? '',
    updated_r_rule: appointment.updated_r_rule ?? null,
    exdate: parsedRule.exdates?.join(','),
  };
}
